import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { PaginatedResponse, UseInfiniteListProps } from "./types";

export function useInfiniteList<T>({
  queryKey,
  fetchFn,
  staleTime = 1000 * 60 * 5,
  refetchOnWindowFocus = false,
  enabled = true,
}: UseInfiniteListProps<T>) {
  const query = useInfiniteQuery<PaginatedResponse<T>, Error>({
    queryKey,
    queryFn: async ({ pageParam }) => {
      return fetchFn(pageParam as number);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length < lastPage.totalPages) {
        return allPages.length + 1;
      }
      return undefined;
    },
    staleTime,
    refetchOnWindowFocus,
    enabled,
  });

  // Stable data reference — only recomputes when pages actually change
  const pages = query.data?.pages;
  const data = useMemo(
    () => pages?.flatMap((page) => page.data) ?? [],
    [pages],
  );

  const hasMore = query.hasNextPage;
  const isBusy = query.isFetchingNextPage || query.isLoading;

  return {
    data,
    loading: query.isFetching,
    hasMore: hasMore && !isBusy,
    loadMore: query.fetchNextPage,
    refetch: query.refetch,
    total: data.length,
    totalPages: query.data?.pages.at(-1)?.totalPages ?? 1,
    error: query.error,
  };
}
